import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { useAuthStore } from '../stores/authStore'
import { useChatStore } from '../stores/chatStore'
import { useUiStore } from '../stores/uiStore'
import { sortChats } from '../lib/chat'
import { signOut } from '../lib/data'
import { ChatListItem } from '../components/ChatListItem'
import { NewChatPicker } from '../components/NewChatPicker'
import { NotificationToggle } from '../components/NotificationToggle'
import { IconButton } from '../components/IconButton'
import { Avatar } from '../components/Avatar'
import { SearchField } from '../components/SearchField'
import { SearchResults } from '../components/SearchResults'
import { ChatIcon, ComposeIcon, LogOutIcon, MoreIcon, PhoneIcon, PlusIcon, SearchIcon, ShieldIcon, UserIcon } from '../components/Icons'
import { useT } from '../lib/i18n'

export function ChatListPane() {
  const navigate = useNavigate()
  const t = useT()
  const user = useAuthStore((state) => state.user)
  const chats = useChatStore((state) => state.chats)
  const socketState = useUiStore((state) => state.socketState)
  const list = useMemo(() => sortChats(Object.values(chats)), [chats])
  const [composing, setComposing] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  /** The list is showing results instead of chats (W3) — one field, one mode. */
  const [searching, setSearching] = useState(false)

  return (
    <div className="flex h-full min-h-0 flex-col bg-surface">
      {/* Four controls and the title. Everything that is a setting lives behind one labelled menu;
          the header keeps the product name, the action that starts the product's main loop, and the
          way in to everything else. Search (W3) replaces the whole row while it is open, which is
          the same gesture on a phone and on the desktop pane, and the call log (W6) is one control
          away beside it — a second list, next to the list you are in. */}
      <header className="app-header flex items-center gap-1 border-b border-line">
        {searching ? (
          <SearchField chatId={null} label={t('search.label')} onClose={() => setSearching(false)} />
        ) : (
          <>
            <h1 className="min-w-0 flex-1 truncate text-title font-semibold tracking-tight text-ink">
              olmt<span className="text-accent">·</span>call
            </h1>
            <IconButton label={t('search.label')} size={40} onClick={() => setSearching(true)}>
              <SearchIcon size={20} />
            </IconButton>
            <IconButton label={t('calls.open')} size={40} onClick={() => void navigate('/calls')}>
              <PhoneIcon size={20} />
            </IconButton>
            <IconButton label={t('chat.newChat')} variant="accent" size={40} onClick={() => setComposing(true)}>
              <ComposeIcon size={20} />
            </IconButton>
            <IconButton
              label={t('chat.menu')}
              size={40}
              pressed={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <MoreIcon size={22} />
            </IconButton>
          </>
        )}
      </header>

      {socketState !== 'open' && (
        <p
          role="status"
          className="mx-3 mt-2 flex items-center gap-2 rounded-panel bg-warn/10 px-3 py-2 text-meta text-warn ring-1 ring-warn/25 ring-inset"
        >
          <span aria-hidden="true" className="size-1.5 rounded-full bg-warn" />
          {socketState === 'connecting' ? t('chat.connecting') : t('chat.offlineNotice')}
        </p>
      )}

      {searching ? (
        <SearchResults
          onSelect={(hit) => {
            setSearching(false)
            // The thread owns the history and the scroller, so the jump is *asked for* here and
            // performed there — one implementation, and its notice lands where the reader ends up.
            useChatStore.getState().requestJump(hit.chat.id, hit.message.id)
            void navigate(`/chat/${hit.chat.id}`)
          }}
        />
      ) : (
        <nav aria-label={t('chat.chats')} className="chat-scroll">
          {list.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-8 py-16 text-center">
              <span
                aria-hidden="true"
                className="mb-1 flex size-14 items-center justify-center rounded-full bg-accent/10 text-accent ring-1 ring-accent/25 ring-inset"
              >
                <ChatIcon size={26} />
              </span>
              <p className="text-body font-semibold text-ink">{t('chat.noChats')}</p>
              <p className="max-w-[22rem] text-ui text-muted">{t('chat.noChatsHint')}</p>
              <button
                type="button"
                onClick={() => setComposing(true)}
                className="press mt-1 inline-flex min-h-11 items-center gap-2 rounded-full bg-accent px-5 text-ui font-semibold text-on-accent hover:bg-accent-strong"
              >
                <PlusIcon size={18} />
                {t('chat.newChat')}
              </button>
            </div>
          ) : (
            <ul>
              {list.map((chat) => (
                <ChatListItem key={chat.id} chat={chat} meId={user?.id ?? null} />
              ))}
            </ul>
          )}
        </nav>
      )}

      {menuOpen && (
        <OverflowMenu
          onClose={() => setMenuOpen(false)}
          user={user}
          isAdmin={user?.isAdmin === true}
          onAdmin={() => {
            setMenuOpen(false)
            // A route, not a link: `/admin` forwards to the console (`VITE_ADMIN_URL`) with one
            // plain navigation, so a bookmark and this row end up in the same place.
            void navigate('/admin')
          }}
          onAccount={() => {
            setMenuOpen(false)
            void navigate('/account')
          }}
          onSignOut={() => {
            setMenuOpen(false)
            void signOut().then(() => navigate('/login', { replace: true }))
          }}
        />
      )}

      {composing && (
        <NewChatPicker
          onClose={() => setComposing(false)}
          onCreated={(chatId) => {
            setComposing(false)
            void navigate(`/chat/${chatId}`)
          }}
        />
      )}
    </div>
  )
}

type OverflowMenuProps = {
  onClose: () => void
  user: { displayName: string; handle?: string; avatarUrl?: string | null } | null
  isAdmin: boolean
  onAdmin: () => void
  onAccount: () => void
  onSignOut: () => void
}

/**
 * The header's overflow. Escape closes it, the first control takes focus on open so a keyboard or
 * switch user is not dropped back at the top of the page, and every row is a word — the label is
 * the control, not a tooltip on a glyph.
 */
function OverflowMenu({ onClose, user, isAdmin, onAdmin, onAccount, onSignOut }: OverflowMenuProps) {
  const t = useT()
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    panelRef.current?.querySelector<HTMLElement>('button:not([disabled])')?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-30" role="presentation" onClick={onClose}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('chat.menu')}
        onClick={(event) => event.stopPropagation()}
        className="panel-enter absolute end-3 top-[calc(var(--header-h)+var(--sat)+4px)] w-64 overflow-hidden rounded-panel bg-surface shadow-lift ring-1 ring-line-strong"
      >
        <div className="flex items-center gap-3 border-b border-line px-3 py-3">
          <Avatar name={user?.displayName ?? t('chat.me')} path={user?.avatarUrl ?? null} size={40} />
          <span className="min-w-0">
            <span className="block truncate text-ui font-semibold text-ink">
              <bdi>{user?.displayName ?? t('chat.me')}</bdi>
            </span>
            {user?.handle && <span className="block truncate text-meta text-muted">@{user.handle}</span>}
          </span>
        </div>
        <div className="flex flex-col py-1">
          <MenuRow icon={<UserIcon size={20} />} label={t('account.title')} onClick={onAccount} />
          <NotificationToggle variant="row" />
          {isAdmin && (
            <MenuRow icon={<ShieldIcon size={20} />} label={t('chat.adminConsole')} onClick={onAdmin} />
          )}
          <MenuRow icon={<LogOutIcon size={20} />} label={t('chat.signOut')} onClick={onSignOut} danger />
        </div>
      </div>
    </div>
  )
}

function MenuRow({
  icon,
  label,
  onClick,
  danger = false,
}: {
  icon: ReactNode
  label: string
  onClick: () => void
  danger?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-h-12 w-full items-center gap-3 px-3 text-start text-ui transition-colors duration-quick hover:bg-surface2 ${
        danger ? 'text-danger' : 'text-ink'
      }`}
    >
      {icon}
      {label}
    </button>
  )
}
